import { Autocomplete, Button, TextField, Typography, useTheme } from '@mui/material';
import { MdMoreVert } from '@teamworksdev/icons';
import { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '../../../../../../trpc/react';
import { cn } from '../../../../../components/utils';
import { useViewer } from '../../../../../context/viewer';
import { AdvancedExerciseSearchModal } from '../../../../block/advanced-search/AdvancedExerciseSearchModal';
import { AddExerciseTablet } from '../create-edit-exercise-sheet/types';

type ExerciseInputProps = {
  onChange: (exercise: AddExerciseTablet | null) => void;
  onBlur: () => void;
  value: AddExerciseTablet | null;
  // Note: we cannot blindly use the useViewer selectedTeam on tablet because the Tablet supports athletes across multiple teams
  teamId: string;
  disabled?: boolean;
  placeholder: string;
  autoFocus?: boolean;
  className?: string;
};

export const ExerciseInput = forwardRef<HTMLInputElement, ExerciseInputProps>(
  ({ className, placeholder, value, onChange, onBlur, teamId, ...props }, ref) => {
    const {
      palette: { greyscaleLight, mediumEmphasisTextLight },
    } = useTheme();
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false);

    const { org: { settings: { enableTeamLibrary } } } = useViewer();

    const { data, isLoading } = trpc.exerciseLibrary.exerciseSearch.useQuery({
      searchTerm: searchTerm,
      teamId,
      limit: 5,
    });

    const handleAdvancedSearchOpen = () => {
      setIsAdvancedSearchOpen(true);
    };
    const handleAdvancedSearchClose = () => {
      setIsAdvancedSearchOpen(false);
    };

    return (
      <>
        <Autocomplete
          {...props}
          ref={ref}
          value={value ?? null}
          onChange={(event, newValue) => {
            // Only pass exercise objects or null, filter out strings from freeSolo
            if (typeof newValue === 'string') {
              onChange(null);
            } else {
              onChange(newValue);
            }
          }}
          forcePopupIcon={false}
          freeSolo
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={placeholder}
              size="medium"
              style={{
                borderColor: greyscaleLight[300],
              }}
              className={cn(
                'flex h-11 w-full rounded-s-md hover:border hover:border-black border bg-transparent text-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
                className,
              )}
              onChange={(event) => {
                setSearchTerm(event.target.value);
              }}
              onBlur={onBlur}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <Button
                    variant="outlined"
                    className="rounded-s-none border-l-0 h-full"
                    sx={{ backgroundColor: greyscaleLight[100] }}
                    onClick={handleAdvancedSearchOpen}
                  >
                    <MdMoreVert color={greyscaleLight[300]} size={24} />
                  </Button>
                ),
              }}
            />
          )}
          renderOption={({ key, ...props }, exercise) => {
            const teamName = exercise.team?.name ?? t('baseLibrary');
            return (
              <li key={key} {...props}>
                <div className="flex flex-col">
                  <span>{exercise.name}</span>
                  {enableTeamLibrary && (
                    <Typography
                      variant="caption"
                      sx={{ color: mediumEmphasisTextLight }}
                    >
                      {teamName}
                    </Typography>
                  )}
                </div>
              </li>
            );
          }}
          noOptionsText={isLoading ? t('loading') : t('noExercisesFound')}
          options={data?.records ?? []}
          getOptionLabel={(option) => {
            if (typeof option === 'string') {
              return option;
            }
            return option.name;
          }}
          style={{
            borderColor: greyscaleLight[300],
            width: '300px',
          }}
          sx={{
            '& .MuiFormControl-root': {
              padding: 0,
              margin: 0,
              height: '44px',
              '& .MuiInputBase-root': {
                height: '44px',
                paddingLeft: '12px',
                paddingRight: '0px',
                paddingY: '0px',
                '& .MuiAutocomplete-input': {
                  padding: 0,
                },
              },
            },
          }}
        />
        {isAdvancedSearchOpen && (
          <AdvancedExerciseSearchModal
            id={'tablet-create-edit-exercise-sheet'}
            initialSearchTerm={searchTerm}
            open={isAdvancedSearchOpen}
            onClose={handleAdvancedSearchClose}
            teamId={teamId}
            onAddExercise={(exercise) => {
              onChange(exercise);
              setSearchTerm('');
              handleAdvancedSearchClose();
            }}
          />
        )}
      </>
    );
  },
);
